import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Logo, { LogoBlue } from './Logo';
import { Button } from './ui/button';
import { Avatar, AvatarFallback } from './ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import { 
  Shield, 
  FileText, 
  PlusCircle, 
  Users, 
  Building2, 
  LogOut,
  Menu,
  ChevronRight
} from 'lucide-react';
import { cn } from '../lib/utils';

const Layout = ({ children }) => {
  const { user, logout, isAdmin, isFleetManager, isDriver } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getInitials = () => {
    if (!user) return 'U';
    return `${user.first_name?.[0] || ''}${user.last_name?.[0] || ''}`.toUpperCase();
  };

  const getRoleName = () => {
    const roleNames = {
      master_admin: 'Master Admin',
      super_admin: 'Super Admin',
      gestionnaire_c2: 'Gestionnaire C2',
      admin_entreprise: 'Admin Entreprise',
      gestionnaire_flotte: 'Gestionnaire Flotte',
      chauffeur: 'Chauffeur'
    };
    return roleNames[user?.role] || user?.role;
  };

  const navItems = [
    { 
      label: 'Mes dossiers', 
      icon: FileText, 
      path: '/claims',
      roles: ['master_admin', 'super_admin', 'gestionnaire_c2', 'admin_entreprise', 'gestionnaire_flotte']
    },
    { 
      label: 'Nouveau dossier', 
      icon: PlusCircle, 
      path: '/claims/new',
      roles: ['master_admin', 'super_admin', 'gestionnaire_c2', 'admin_entreprise', 'gestionnaire_flotte']
    },
    { 
      label: 'Clients', 
      icon: Building2, 
      path: '/clients',
      roles: ['master_admin', 'super_admin', 'gestionnaire_c2']
    },
    { 
      label: 'Utilisateurs', 
      icon: Users, 
      path: '/users',
      roles: ['master_admin']
    }
  ];

  const filteredNavItems = navItems.filter(item => 
    item.roles.includes(user?.role)
  );

  const NavLink = ({ item, mobile = false }) => {
    const isActive = location.pathname === item.path || 
      (item.path !== '/dashboard' && location.pathname.startsWith(item.path));
    
    return (
      <Link
        to={item.path}
        onClick={() => mobile && setMobileOpen(false)}
        className={cn(
          "flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200",
          isActive 
            ? "bg-slate-900 text-white shadow-md" 
            : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
        )}
        data-testid={`nav-${item.path.replace('/', '')}`}
      >
        <item.icon className={cn("w-5 h-5", isActive && "text-amber-400")} />
        <span className="font-medium">{item.label}</span>
        {isActive && <ChevronRight className="w-4 h-4 ml-auto" />}
      </Link>
    );
  };

  const SidebarContent = ({ mobile = false }) => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="p-6 border-b border-slate-100">
        <Link to="/dashboard" onClick={() => mobile && setMobileOpen(false)}>
          <LogoBlue size="default" />
        </Link>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        {filteredNavItems.map((item) => (
          <NavLink key={item.path} item={item} mobile={mobile} />
        ))}
      </nav>

      {/* User Info */}
      <div className="p-4 border-t border-slate-100">
        <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
          <Avatar className="h-10 w-10 bg-slate-900">
            <AvatarFallback className="bg-slate-900 text-white text-sm font-semibold">
              {getInitials()}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-slate-900 truncate">
              {user?.first_name} {user?.last_name}
            </p>
            <p className="text-xs text-slate-500 truncate">{getRoleName()}</p>
          </div>
        </div>
      </div>
    </div>
  );

  // Ultra simple layout for drivers - no sidebar, no header, just content
  if (isDriver()) {
    return (
      <div className="min-h-screen bg-slate-50">
        {/* Simple header with logout only */}
        <header className="sticky top-0 z-40 flex items-center justify-between h-14 px-4 bg-white border-b border-slate-200">
          <LogoBlue size="small" />
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={handleLogout}
            className="text-slate-500"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Déconnexion
          </Button>
        </header>
        <main>
          {children}
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:fixed lg:inset-y-0 lg:left-0 lg:flex lg:w-64 lg:flex-col bg-white border-r border-slate-200">
        <SidebarContent />
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden sticky top-0 z-40 flex items-center justify-between h-16 px-4 bg-white border-b border-slate-200">
        <div className="flex items-center gap-3">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="mobile-menu-btn">
                <Menu className="w-5 h-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72 p-0">
              <SidebarContent mobile />
            </SheetContent>
          </Sheet>
          <LogoBlue size="small" />
        </div>
        
        <div className="flex items-center gap-1">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="rounded-full" data-testid="user-menu-btn">
                <Avatar className="h-8 w-8">
                  <AvatarFallback className="bg-slate-900 text-white text-xs">
                    {getInitials()}
                  </AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div>
                  <p className="font-medium">{user?.first_name} {user?.last_name}</p>
                  <p className="text-xs text-slate-500">{user?.email}</p>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="logout-btn">
                <LogOut className="w-4 h-4 mr-2" />
                Déconnexion
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Main Content */}
      <main className="lg:pl-64">
        {/* Desktop Header */}
        <header className="hidden lg:flex items-center justify-between h-16 px-8 bg-white border-b border-slate-200">
          <div>
            {user?.organization_name && (
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <Building2 className="w-4 h-4" />
                <span>{user.organization_name}</span>
              </div>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2" data-testid="desktop-user-menu">
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="bg-slate-900 text-white text-xs">
                      {getInitials()}
                    </AvatarFallback>
                  </Avatar>
                  <span className="text-sm font-medium">{user?.first_name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div>
                    <p className="font-medium">{user?.first_name} {user?.last_name}</p>
                    <p className="text-xs text-slate-500">{user?.email}</p>
                    <p className="text-xs text-amber-600 mt-1">{getRoleName()}</p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="desktop-logout-btn">
                  <LogOut className="w-4 h-4 mr-2" />
                  Déconnexion
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page Content */}
        <div className="p-4 md:p-8">
          {children}
        </div>
      </main>
    </div>
  );
};

export default Layout;
